import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Card,
  Chip,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Stack,
  Fab,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { format } from 'date-fns';
import { useIsMobile } from '@/hooks/useIsMobile';
import { DataTable, type DataTableColumn, type QuickFilter } from '@/components/common';
import type { PurchaseOrder, PurchaseOrderItem, PurchaseOrderStatus, Supplier, InventoryItem } from '@/types';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import { useNotify } from '@/stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { downloadCsv } from '@/utils/csv';
import {
  PurchaseOrderDetailModal,
  PurchaseOrderFormDialog,
  PurchaseOrderReceiveDialog,
  MatchInvoiceDialog,
  type PurchaseOrderFormData,
} from '@/components/purchase-orders';
import {
  usePurchaseOrders,
  usePurchaseOrder,
  useSuppliers,
  useInventoryParts,
  useCreatePurchaseOrder,
  useUpdatePurchaseOrder,
  useUpdatePurchaseOrderStatus,
  useReceivePurchaseOrder,
  useMatchPurchaseOrderInvoice,
  useWarehouses,
  type PurchaseOrderListItem,
  type PurchaseOrderLineItem,
  type SupplierListItem,
} from '@/hooks';
import { dateFnsLocale } from '@/utils/locale';

type TabValue = 'all' | PurchaseOrderStatus;

// Map API PO to frontend PurchaseOrder shape. `warehouseId` and real `items`
// aren't part of the list projection — populated separately from
// usePurchaseOrder(id) once a specific PO is selected (see
// `selectedPOWithDetail` below), mirroring how PurchaseOrderReceiveDialog/
// ReturnFormDialog already fetch detail before rendering lines.
function mapPOListItem(po: PurchaseOrderListItem): PurchaseOrder {
  return {
    id: po.id,
    orderNumber: po.orderNumber,
    supplierId: po.supplierId,
    warehouseId: '',
    supplier: po.supplier as Supplier,
    status: po.status as PurchaseOrderStatus,
    items: [],
    subtotalCents: po.subtotalCents,
    taxCents: po.taxCents,
    shippingCents: po.shippingCents,
    totalCents: po.totalCents,
    notes: po.notes ?? undefined,
    createdById: '',
    createdByName: po.createdByName,
    expectedDeliveryDate: po.expectedDeliveryDate ?? undefined,
    receivedAt: po.actualDeliveryDate ?? undefined,
    // Invoice-match fields aren't part of the list projection either —
    // populated from usePurchaseOrder(id) in selectedPOWithDetail below.
    invoiceMatched: false,
    createdAt: po.createdAt,
    updatedAt: po.updatedAt,
  };
}

// Map a real PO-detail line (from usePurchaseOrder) to the frontend line shape.
function mapPOLineItem(line: PurchaseOrderLineItem): PurchaseOrderItem {
  return {
    id: line.id,
    inventoryItemId: line.partId,
    partNumber: line.partNumber,
    name: line.name,
    quantity: line.quantity,
    unitCostCents: line.unitCostCents,
    totalCostCents: line.totalCostCents,
    receivedQuantity: line.quantityReceived,
    linkedBookingId: line.linkedBookingId,
    linkedWorkOrder: line.linkedWorkOrder,
  };
}

function mapSupplierToFrontend(s: SupplierListItem): Supplier {
  return {
    id: s.id,
    name: s.name,
    deliveryDays: s.defaultLeadTimeDays,
    phone: s.phone ?? undefined,
    email: s.email ?? undefined,
  };
}

function mapPartToInventoryItem(part: { id: string; sku: string; name: string; sellPriceCents: number; costPriceCents: number; createdAt: string; updatedAt: string }): InventoryItem {
  return {
    id: part.id,
    partNumber: part.sku,
    name: part.name,
    unitPriceCents: part.sellPriceCents,
    costPriceCents: part.costPriceCents,
    category: 'other',
    quantity: 0,
    minStockLevel: 0,
    createdAt: part.createdAt,
    updatedAt: part.updatedAt,
  };
}

const statusColors: Record<PurchaseOrderStatus, 'default' | 'warning' | 'info' | 'primary' | 'success' | 'error'> = {
  draft: 'default',
  approved: 'info',
  ordered: 'primary',
  partially_received: 'warning',
  received: 'success',
  cancelled: 'error',
};

export default function PurchaseOrdersPage() {
  const notify = useNotify();
  const isMobile = useIsMobile();
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const canCreatePurchaseOrder = useAuthStore((s) => s.hasPermission('purchase_orders.create'));

  const statusLabels: Record<PurchaseOrderStatus, string> = {
    draft: t('purchaseOrders.status.draft'),
    approved: t('purchaseOrders.status.approved'),
    ordered: t('purchaseOrders.status.ordered'),
    partially_received: t('purchaseOrders.status.partiallyReceived'),
    received: t('purchaseOrders.status.received'),
    cancelled: t('purchaseOrders.status.cancelled'),
  };

  // Fetch purchase orders from API
  const poQuery = usePurchaseOrders();
  const purchaseOrders = useMemo(() => {
    if (!poQuery.data?.pages) return [];
    return poQuery.data.pages.flatMap((page) => page.data.map(mapPOListItem));
  }, [poQuery.data]);

  // Fetch suppliers from API
  const suppliersQuery = useSuppliers();
  const suppliersList: Supplier[] = useMemo(() => {
    if (!suppliersQuery.data?.pages) return [];
    return suppliersQuery.data.pages.flatMap((page) => page.data.map(mapSupplierToFrontend));
  }, [suppliersQuery.data]);

  // Fetch inventory parts for form dialog
  const partsQuery = useInventoryParts();
  const inventory: InventoryItem[] = useMemo(() => {
    if (!partsQuery.data?.pages) return [];
    return partsQuery.data.pages.flatMap((page) => page.data.map(mapPartToInventoryItem));
  }, [partsQuery.data]);

  // Warehouses for the create/edit form's destination picker
  const warehousesQuery = useWarehouses();
  const warehousesList = warehousesQuery.data?.data ?? [];

  // Mutations
  const createMutation = useCreatePurchaseOrder();
  const updateMutation = useUpdatePurchaseOrder();
  const updateStatusMutation = useUpdatePurchaseOrderStatus();
  const receiveMutation = useReceivePurchaseOrder();
  const matchInvoiceMutation = useMatchPurchaseOrderInvoice();
  const [tab, setTab] = useState<TabValue>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [matchInvoiceDialogOpen, setMatchInvoiceDialogOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuPO, setMenuPO] = useState<PurchaseOrder | null>(null);
  /** Purchase order awaiting cancel confirmation (#2722). */
  const [cancelTarget, setCancelTarget] = useState<PurchaseOrder | null>(null);

  // Real line-level detail (items, warehouseId) for whichever PO is selected —
  // the list projection never carries these (see mapPOListItem above).
  const poDetailQuery = usePurchaseOrder(selectedPO?.id);
  // The edit form must not open on stale (items-less) data — it would
  // initialize empty and then wipe any in-progress user edits once detail
  // arrives and the form's init effect re-runs (same race as #724's part-edit
  // fix). The read-only detail modal has no such risk and isn't gated.
  const isFormDataReady = !selectedPO || (poDetailQuery.data?.id === selectedPO.id);
  const selectedPOWithDetail = useMemo<PurchaseOrder | null>(() => {
    if (!selectedPO) return null;
    if (poDetailQuery.data && poDetailQuery.data.id === selectedPO.id) {
      return {
        ...selectedPO,
        warehouseId: poDetailQuery.data.warehouseId,
        items: poDetailQuery.data.items.map(mapPOLineItem),
        approvedByName: poDetailQuery.data.approvedByName,
        approvedAt: poDetailQuery.data.approvedAt,
        supplierInvoiceNumber: poDetailQuery.data.supplierInvoiceNumber,
        supplierInvoiceDate: poDetailQuery.data.supplierInvoiceDate,
        supplierInvoiceTotalCents: poDetailQuery.data.supplierInvoiceTotalCents,
        invoiceMatched: poDetailQuery.data.invoiceMatched,
        invoiceDiscrepancyCents: poDetailQuery.data.invoiceDiscrepancyCents,
        invoiceMatchedAt: poDetailQuery.data.invoiceMatchedAt,
        invoiceMatchedByName: poDetailQuery.data.invoiceMatchedByName,
      };
    }
    return selectedPO;
  }, [selectedPO, poDetailQuery.data]);

  // Filter and search
  const filteredOrders = useMemo(() => {
    let filtered = purchaseOrders;

    // Filter by tab
    if (tab !== 'all') {
      filtered = filtered.filter((po) => po.status === tab);
    }

    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (po) =>
          po.orderNumber.toLowerCase().includes(query) ||
          po.supplier.name.toLowerCase().includes(query) ||
          po.items.some((item) => item.partNumber.toLowerCase().includes(query) || item.name.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [purchaseOrders, tab, searchQuery]);

  // Count by status
  const statusCounts = useMemo(() => {
    const counts: Record<TabValue, number> = {
      all: purchaseOrders.length,
      draft: 0,
      approved: 0,
      ordered: 0,
      partially_received: 0,
      received: 0,
      cancelled: 0,
    };

    purchaseOrders.forEach((po) => {
      counts[po.status]++;
    });

    return counts;
  }, [purchaseOrders]);

  const quickFilters: QuickFilter[] = [
    { label: `${t('purchaseOrders.tabAll')} (${statusCounts.all})`, value: 'all' },
    { label: `${t('purchaseOrders.tabDrafts')} (${statusCounts.draft})`, value: 'draft' },
    { label: `${t('purchaseOrders.tabApproved')} (${statusCounts.approved})`, value: 'approved' },
    { label: `${t('purchaseOrders.tabOrdered')} (${statusCounts.ordered})`, value: 'ordered' },
    { label: `${t('purchaseOrders.tabPartiallyReceived')} (${statusCounts.partially_received})`, value: 'partially_received' },
    { label: `${t('purchaseOrders.tabReceived')} (${statusCounts.received})`, value: 'received' },
  ];

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, po: PurchaseOrder) => {
    setAnchorEl(event.currentTarget);
    setMenuPO(po);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuPO(null);
  };

  const handleView = (po: PurchaseOrder) => {
    setSelectedPO(po);
    setDetailModalOpen(true);
    handleMenuClose();
  };

  const handleEdit = (po: PurchaseOrder) => {
    setSelectedPO(po);
    setFormDialogOpen(true);
    handleMenuClose();
  };

  const handleCreate = () => {
    setSelectedPO(null);
    setFormDialogOpen(true);
  };

  const handleApprove = (po: PurchaseOrder) => {
    updateStatusMutation.mutate(
      { id: po.id, status: 'approved' },
      {
        onSuccess: () => notify.success(t('purchaseOrders.approveSuccess'), `${po.orderNumber} ${t('purchaseOrders.status.approved').toLowerCase()}`),
        onError: () => notify.error(tc('error'), t('purchaseOrders.approveFailed')),
      },
    );
    handleMenuClose();
  };

  const handleMarkOrdered = (po: PurchaseOrder) => {
    updateStatusMutation.mutate(
      { id: po.id, status: 'ordered' },
      {
        onSuccess: () => notify.success(t('purchaseOrders.orderSent'), t('purchaseOrders.orderSentDesc', { orderNumber: po.orderNumber })),
        onError: () => notify.error(tc('error'), t('purchaseOrders.statusUpdateFailed')),
      },
    );
    handleMenuClose();
  };

  const handleMarkReceived = (po: PurchaseOrder) => {
    setSelectedPO(po);
    setReceiveDialogOpen(true);
    handleMenuClose();
  };

  const handleConfirmReceive = (lines: { lineId: string; quantityReceived: number }[]) => {
    if (!selectedPO) return;
    receiveMutation.mutate(
      { id: selectedPO.id, lines },
      {
        onSuccess: (result) => {
          const stockCount = result.lines.filter((l) => l.destination === 'stock').length;
          const transitCount = result.lines.filter((l) => l.destination === 'transit').length;
          const partialCount = result.lines.filter((l) => !l.lineComplete).length;
          const details = [
            stockCount > 0 ? t('purchaseOrderReceive.resultStockUpdated', { count: stockCount }) : null,
            transitCount > 0 ? t('purchaseOrderReceive.resultTransitCreated', { count: transitCount }) : null,
            partialCount > 0 ? t('purchaseOrderReceive.resultPartialOpen', { count: partialCount }) : null,
          ]
            .filter(Boolean)
            .join(' · ');
          notify.success(
            t('purchaseOrders.orderReceived'),
            details || t('purchaseOrders.orderReceivedDesc', { orderNumber: selectedPO.orderNumber }),
          );
          setReceiveDialogOpen(false);
        },
        onError: () => notify.error(tc('error'), t('purchaseOrders.receiveFailed')),
      },
    );
  };

  const handleCloseReceiveDialog = () => {
    setReceiveDialogOpen(false);
  };

  const handleMatchInvoice = (po: PurchaseOrder) => {
    setSelectedPO(po);
    setMatchInvoiceDialogOpen(true);
    handleMenuClose();
  };

  const handleConfirmMatchInvoice = (data: { invoiceNumber: string; invoiceDate: string; invoiceTotalCents: number }) => {
    if (!selectedPO) return;
    matchInvoiceMutation.mutate(
      { id: selectedPO.id, data },
      {
        onSuccess: (result) => {
          notify.success(
            result.isExactMatch
              ? t('purchaseOrderDetail.invoiceMatch.successExact')
              : t('purchaseOrderDetail.invoiceMatch.successDiscrepancy', {
                  amount: (result.discrepancyCents / 100).toFixed(2),
                }),
            selectedPO.orderNumber,
          );
          setMatchInvoiceDialogOpen(false);
        },
        onError: () => notify.error(tc('error'), t('purchaseOrderDetail.invoiceMatch.failed')),
      },
    );
  };

  const handleCloseMatchInvoiceDialog = () => {
    setMatchInvoiceDialogOpen(false);
  };

  /**
   * Cancelling a purchase order, behind a confirmation (#2722).
   *
   * Two problems, both fixed here. The red menu item fired a server status
   * change on the first click with no ConfirmDialog — and it was labelled
   * "Poista" while the effect is a transition to `cancelled`, so the label
   * promised something different from what happened.
   */
  const handleRequestCancel = (po: PurchaseOrder) => {
    setCancelTarget(po);
    handleMenuClose();
  };

  const handleConfirmCancel = () => {
    const po = cancelTarget;
    if (!po) return;
    updateStatusMutation.mutate(
      { id: po.id, status: 'cancelled' },
      {
        onSuccess: () => notify.success(t('purchaseOrders.orderCancelled'), `${po.orderNumber}`),
        onError: () => notify.error(tc('error'), t('purchaseOrders.cancelFailed')),
      },
    );
    setCancelTarget(null);
  };

  /**
   * Export exactly the rows on screen (#2722).
   *
   * Was a lone `notify.info` promising "Ostotilaukset viedään
   * CSV-tiedostoon..." while producing nothing. Exports the filtered list
   * rather than everything, so what lands in the file is what the user is
   * looking at.
   */
  const handleExport = () => {
    if (filteredOrders.length === 0) {
      notify.warning(t('purchaseOrders.exportEmpty'), t('purchaseOrders.exportEmptyDesc'));
      return;
    }
    downloadCsv(
      `${t('purchaseOrders.exportFilename')}-${new Date().toISOString().slice(0, 10)}`,
      [
        t('purchaseOrders.colOrderNumber'),
        t('purchaseOrders.colSupplier'),
        t('purchaseOrders.colStatus'),
        t('purchaseOrders.colTotal'),
        t('purchaseOrders.colCreated'),
      ],
      filteredOrders.map((po) => [
        po.orderNumber,
        po.supplier?.name ?? '',
        statusLabels[po.status] ?? po.status,
        po.totalCents / 100,
        po.orderedAt ?? po.createdAt ?? '',
      ]),
    );
    notify.success(
      t('purchaseOrders.exportReady'),
      t('purchaseOrders.exportReadyDesc', { count: filteredOrders.length }),
    );
  };

  const handleSaveForm = (data: PurchaseOrderFormData, submitForApproval: boolean) => {
    const lines = data.items.map((item) => ({
      partId: item.inventoryItemId ?? '',
      quantityOrdered: item.quantity,
      unitPriceCents: item.unitCostCents,
      ...(item.linkedBookingId ? { linkedBookingId: item.linkedBookingId } : {}),
    }));

    // Editing a draft only ever updates its lines/details — "submit for
    // approval" has no separate meaning here beyond what create already does
    // (see below), since there's no distinct "pending approval" state to move
    // a draft into via this dialog.
    if (selectedPO?.status === 'draft') {
      updateMutation.mutate(
        {
          id: selectedPO.id,
          data: {
            supplierId: data.supplierId,
            warehouseId: data.warehouseId,
            notes: data.notes ?? null,
            shippingCents: data.shippingCents,
            lines,
          },
        },
        {
          onSuccess: () => notify.success(t('purchaseOrders.orderUpdated'), selectedPO.orderNumber),
          onError: (error) => notify.error(tc('error'), describeApiError(error, tc, { fallback: t('purchaseOrders.updateFailed') })),
        },
      );
      return;
    }

    createMutation.mutate(
      {
        supplierId: data.supplierId,
        warehouseId: data.warehouseId,
        notes: data.notes,
        shippingCents: data.shippingCents,
        lines,
      },
      {
        onSuccess: (result) => {
          const statusText = submitForApproval ? t('purchaseOrders.sentForApproval') : t('purchaseOrders.asDraft');
          notify.success(t('purchaseOrders.orderCreated'), statusText);
          // "Submit for approval" creates the draft, then immediately
          // transitions it to approved — the backend has no separate
          // "pending" state to submit into.
          if (submitForApproval) {
            updateStatusMutation.mutate({ id: result.id, status: 'approved' });
          }
        },
        onError: () => notify.error(tc('error'), t('purchaseOrders.createFailed')),
      },
    );
  };

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedPO(null);
  };

  const handleCloseFormDialog = () => {
    setFormDialogOpen(false);
    if (!formDialogOpen) {
      setSelectedPO(null);
    }
  };

  const handleEditFromDetail = (po: PurchaseOrder) => {
    setDetailModalOpen(false);
    setSelectedPO(po);
    setFormDialogOpen(true);
  };

  const handleApproveFromDetail = (po: PurchaseOrder) => {
    handleApprove(po);
    setDetailModalOpen(false);
  };

  const handleMarkOrderedFromDetail = (po: PurchaseOrder) => {
    handleMarkOrdered(po);
    setDetailModalOpen(false);
  };

  const handleMarkReceivedFromDetail = (po: PurchaseOrder) => {
    handleMarkReceived(po);
    setDetailModalOpen(false);
  };

  const handleMatchInvoiceFromDetail = (po: PurchaseOrder) => {
    handleMatchInvoice(po);
    setDetailModalOpen(false);
  };

  const handlePrint = (po: PurchaseOrder) => {
    notify.info(t('purchaseOrders.printing'), `${po.orderNumber}`);
  };

  const poSortCompareFn = (a: PurchaseOrder, b: PurchaseOrder, key: string): number => {
    switch (key) {
      case 'orderNumber':
        return a.orderNumber.localeCompare(b.orderNumber);
      case 'supplier':
        return a.supplier.name.localeCompare(b.supplier.name, 'fi');
      case 'total':
        return a.totalCents - b.totalCents;
      case 'created':
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      case 'deliveryDate': {
        const dateA = a.expectedDeliveryDate ? new Date(a.expectedDeliveryDate).getTime() : 0;
        const dateB = b.expectedDeliveryDate ? new Date(b.expectedDeliveryDate).getTime() : 0;
        return dateA - dateB;
      }
      default:
        return 0;
    }
  };

  const columns: DataTableColumn<PurchaseOrder>[] = [
    {
      header: t('purchaseOrders.colOrderNumber'),
      sortKey: 'orderNumber',
      renderCell: (po) => (
        <>
          <Typography variant="body2" fontWeight={600}>
            {po.orderNumber}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {po.createdByName}
          </Typography>
        </>
      ),
    },
    {
      header: t('purchaseOrders.colSupplier'),
      sortKey: 'supplier',
      renderCell: (po) => (
        <>
          <Typography variant="body2">{po.supplier.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t('purchaseOrders.deliveryDays', { days: po.supplier.deliveryDays })}
          </Typography>
        </>
      ),
    },
    {
      header: t('purchaseOrders.colParts'),
      renderCell: (po) => (
        <>
          <Typography variant="body2">{t('purchaseOrders.partsCount', { count: po.items.length })}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t('purchaseOrders.totalPcs', { count: po.items.reduce((sum, item) => sum + item.quantity, 0) })}
          </Typography>
        </>
      ),
    },
    {
      header: t('purchaseOrders.colTotal'),
      sortKey: 'total',
      align: 'right',
      renderCell: (po) => (
        <>
          <Typography variant="body2" fontWeight={600}>
            €{(po.totalCents / 100).toFixed(2)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('purchaseOrders.vatIncluded')}
          </Typography>
        </>
      ),
    },
    {
      header: t('purchaseOrders.colStatus'),
      renderCell: (po) => (
        <Chip label={statusLabels[po.status]} size="small" color={statusColors[po.status]} />
      ),
    },
    {
      header: t('purchaseOrders.colCreated'),
      sortKey: 'created',
      renderCell: (po) => (
        <>
          <Typography variant="body2">
            {format(new Date(po.createdAt), 'd.M.yyyy', { locale: dateFnsLocale() })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {format(new Date(po.createdAt), 'HH:mm')}
          </Typography>
        </>
      ),
    },
    {
      header: t('purchaseOrders.colDeliveryDate'),
      sortKey: 'deliveryDate',
      renderCell: (po) =>
        po.expectedDeliveryDate ? (
          <>
            <Typography variant="body2">
              {format(new Date(po.expectedDeliveryDate), 'd.M.yyyy', { locale: dateFnsLocale() })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('purchaseOrders.estimated')}
            </Typography>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        ),
    },
    {
      header: '',
      align: 'right',
      renderCell: (po) => (
        <Tooltip title={tc('moreOptions')}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, po); }} aria-label={tc('moreOptions')}>
            <MoreVertIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            {t('purchaseOrders.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('purchaseOrders.subtitle')}
          </Typography>
        </Box>
        {!isMobile && (
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExport}>
              {t('purchaseOrders.export')}
            </Button>
            {canCreatePurchaseOrder && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
                {t('purchaseOrders.newOrder')}
              </Button>
            )}
          </Stack>
        )}
      </Box>

      <Card>
        {/* Search */}
        <Box sx={{ p: 2, pb: 0 }}>
          <TextField
            fullWidth
            placeholder={t('purchaseOrders.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Quick Filters (replaces Tabs) */}
        <Box sx={{ px: 2, pt: 2 }}>
          <DataTable<PurchaseOrder>
            columns={columns}
            data={filteredOrders}
            getRowKey={(po) => po.id}
            sortCompareFn={poSortCompareFn}
            defaultSortKey="created"
            defaultSortDirection="desc"
            onRowClick={handleView}
            quickFilters={quickFilters}
            activeFilter={tab}
            onFilterChange={(value) => setTab(value as TabValue)}
            paperWrapper={false}
            emptyMessage={searchQuery ? t('purchaseOrders.noResults') : t('purchaseOrders.noOrders')}
            // No guidance when a search found nothing — the user knows why.
            emptyDescription={searchQuery ? undefined : t('purchaseOrders.noOrdersDesc')}
            renderMobileCard={(po) => (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {po.orderNumber}
                  </Typography>
                  <Chip label={statusLabels[po.status]} size="small" color={statusColors[po.status]} />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {po.supplier.name}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight={600}>
                    €{(po.totalCents / 100).toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {format(new Date(po.createdAt), 'd.M.yyyy', { locale: dateFnsLocale() })}
                  </Typography>
                </Box>
              </Box>
            )}
          />
        </Box>
      </Card>

      {/* Context Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => menuPO && handleView(menuPO)}>
          <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
          {t('purchaseOrders.viewDetails')}
        </MenuItem>
        {menuPO?.status === 'draft' && (
          <MenuItem onClick={() => menuPO && handleEdit(menuPO)}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            {tc('edit')}
          </MenuItem>
        )}
        {menuPO?.status === 'draft' && (
          <MenuItem onClick={() => menuPO && handleApprove(menuPO)}>
            <CheckCircleIcon fontSize="small" sx={{ mr: 1 }} />
            {t('purchaseOrders.approve')}
          </MenuItem>
        )}
        {menuPO?.status === 'approved' && (
          <MenuItem onClick={() => menuPO && handleMarkOrdered(menuPO)}>
            <LocalShippingIcon fontSize="small" sx={{ mr: 1 }} />
            {t('purchaseOrders.markOrdered')}
          </MenuItem>
        )}
        {(menuPO?.status === 'ordered' || menuPO?.status === 'partially_received') && (
          <MenuItem onClick={() => menuPO && handleMarkReceived(menuPO)}>
            <CheckCircleIcon fontSize="small" sx={{ mr: 1 }} />
            {t('purchaseOrders.markReceived')}
          </MenuItem>
        )}
        {menuPO?.status === 'received' && !menuPO.invoiceMatched && (
          <MenuItem onClick={() => { handleMatchInvoice(menuPO); }}>
            <ReceiptLongIcon fontSize="small" sx={{ mr: 1 }} />
            {t('purchaseOrderDetail.invoiceMatch.matchButton')}
          </MenuItem>
        )}
        {menuPO?.status === 'draft' && (
          <MenuItem onClick={() => menuPO && handleRequestCancel(menuPO)} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            {/* "Peruuta tilaus", not "Poista": the row is not removed, its
                status becomes cancelled (#2722). */}
            {t('purchaseOrders.cancelOrder')}
          </MenuItem>
        )}
      </Menu>

      {/* Detail Modal */}
      <PurchaseOrderDetailModal
        open={detailModalOpen}
        onClose={handleCloseDetailModal}
        purchaseOrder={selectedPOWithDetail}
        onEdit={handleEditFromDetail}
        onApprove={handleApproveFromDetail}
        onMarkOrdered={handleMarkOrderedFromDetail}
        onMarkReceived={handleMarkReceivedFromDetail}
        onPrint={handlePrint}
        onMatchInvoice={handleMatchInvoiceFromDetail}
      />

      {/* Form Dialog */}
      <PurchaseOrderFormDialog
        open={formDialogOpen && isFormDataReady}
        onClose={handleCloseFormDialog}
        purchaseOrder={selectedPOWithDetail}
        suppliers={suppliersList}
        inventory={inventory}
        warehouses={warehousesList}
        onSave={handleSaveForm}
      />

      {/* Receive Dialog */}
      <PurchaseOrderReceiveDialog
        open={receiveDialogOpen}
        onClose={handleCloseReceiveDialog}
        purchaseOrderId={selectedPO?.id ?? null}
        orderNumber={selectedPO?.orderNumber}
        onConfirm={handleConfirmReceive}
        submitting={receiveMutation.isPending}
      />

      {/* Match Invoice Dialog */}
      <MatchInvoiceDialog
        open={matchInvoiceDialogOpen}
        onClose={handleCloseMatchInvoiceDialog}
        orderNumber={selectedPO?.orderNumber ?? ''}
        poTotalCents={selectedPO?.totalCents ?? 0}
        onConfirm={handleConfirmMatchInvoice}
        isLoading={matchInvoiceMutation.isPending}
      />

      {/* Mobile FAB */}
      {isMobile && canCreatePurchaseOrder && (
        <Fab
          color="primary"
          size="medium"
          onClick={handleCreate}
          sx={{ position: 'fixed', bottom: 72, right: 16, zIndex: 1050 }}
          aria-label={t('purchaseOrders.newOrder')}
        >
          <AddIcon />
        </Fab>
      )}

      {/*
        The label says "Peruuta tilaus" and the effect is a cancel — they
        used to disagree, and there was no confirmation at all (#2722).
      */}
      <ConfirmDialog
        open={cancelTarget !== null}
        title={t('purchaseOrders.cancelOrderTitle')}
        message={t('purchaseOrders.cancelOrderConfirm', { orderNumber: cancelTarget?.orderNumber ?? '' })}
        confirmText={t('purchaseOrders.cancelOrder')}
        confirmColor="error"
        onConfirm={handleConfirmCancel}
        onCancel={() => { setCancelTarget(null); }}
      />
    </Box>
  );
}
